import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import {
  Currency,
  ResponseError,
  responseErrorsResolver,
  useQueryState,
} from '@ui/src';
import { Beneficiary, BeneficiaryContract, BeneficiaryStatus } from '../types';
import {
  useLazyGetBeneficiaryDocumentFileQuery,
  useLazyGetCustomerBeneficiaryContractsQuery,
  useLazyGetCustomerBeneficiaryQuery,
  useLazyGetCustomersBeneficairyDocumentsCategoriesQuery,
  useUpdateBeneficiaryStatusMutation,
  useValidateCustomerBeneficiaryMutation,
} from '../../../customers/store/services/customers';
import { schema } from '../variables/contracts';
import { OrganizationType } from '../../types';
import formatBeneficiaryDocumentsList from '../helpers/format-beneficiary-documents-list';
import formatBeneficiaryContractsList from '../helpers/format-contract-list';

import ROUTES from '../../../../routes';

const useBeneficiary = ({
  beneficiaryId,
  beneficiary,
  documentsCategories,
  beneficiaryContracts,
  type,
  customerId,
  baseCurrency,
  currenciesList,
}: {
  beneficiaryId?: string;
  beneficiary?: Beneficiary;
  documentsCategories?: { id: string; name: string }[];
  beneficiaryContracts?: BeneficiaryContract[];
  type: OrganizationType;
  customerId?: string;
  baseCurrency?: Currency;
  currenciesList?: Currency[];
}) => {
  const [beneficiaryStatus, setBeneficiaryStatus] = useState<
    BeneficiaryStatus | undefined
  >(undefined);
  const [updateStatusError, setUpdateStatusError] = useState('');
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectCommentError, setRejectCommentError] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [globalValidateError, setGlobalValidateError] = useState('');

  const [getBeneficiary, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiaryQuery();
  const [updateBeneficiaryStatus, { isLoading: statusInProgress }] =
    useUpdateBeneficiaryStatusMutation();
  const [
    getBeneficiaryDocumentsCategories,
    {
      isLoading: documentsCategoriesIsLoading,
      isFetching: documentsCategoriesIsFetching,
    },
  ] = useLazyGetCustomersBeneficairyDocumentsCategoriesQuery();
  const [
    getBeneficiaryContracts,
    { isLoading: contractsIsLoading, isFetching: contractsIsFetching },
  ] = useLazyGetCustomerBeneficiaryContractsQuery();
  const [
    getBeneficiaryDocumentFile,
    { isLoading: documentIsLoading, isFetching: documentIsFetching, isSuccess },
  ] = useLazyGetBeneficiaryDocumentFileQuery();
  const [validateCustomerBeneficiary] =
    useValidateCustomerBeneficiaryMutation();

  const {
    formState: { errors },
    setError,
  } = useForm<Beneficiary>();

  const { filters } = useQueryState(schema);

  const onSaveStatus = async () => {
    const body: {
      status: BeneficiaryStatus;
      comment?: string;
    } = {
      status: beneficiaryStatus as BeneficiaryStatus,
    };

    if (rejectComment) {
      body.comment = rejectComment;
    }

    const response = (await updateBeneficiaryStatus({
      id: beneficiaryId as string,
      body,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.error && response.error.data.message) {
      if (
        Array.isArray(response.error.data.message) &&
        response.error.data.message[0].property === 'coreError'
      ) {
        setUpdateStatusError(response.error.data.message[0].value as string);
      } else if (typeof response.error.data.message === 'string') {
        setUpdateStatusError(response.error.data.message);
      } else {
        setUpdateStatusError('Internal Server Error. Try Again');
      }
    } else if (!response.error && rejectPopupVisible) {
      setRejectPopupVisible(false);
      setRejectComment('');
      setRejectCommentError('');
    }
  };

  const onValidateCustomerBeneficiary = useCallback(async (id: string) => {
    const response = (await validateCustomerBeneficiary(id)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setGlobalValidateError,
      });
    }
  }, []);

  const onRowClick = async (id: string) => {
    const currentDocument = beneficiary?.documents?.edges?.find(
      (item) => item.node.id === id,
    );

    setSelectedDocumentId(id);

    if (currentDocument) {
      const response = await getBeneficiaryDocumentFile(
        currentDocument.node.uploadId,
      );

      if (response.data) {
        const hiddenElement = document.createElement('a');

        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = currentDocument?.node.name;
        hiddenElement.click();
      }
    }
  };

  const formattedDocumentsData = useMemo(() => {
    const data = formatBeneficiaryDocumentsList(
      beneficiary?.documents?.edges?.map((item) => item.node) as {
        id: string;
        documentCategoryId: string;
        name: string;
        uploadId: string;
        render?: Record<string, React.JSX.Element>;
      }[],
      documentsCategories as { id: string; name: string }[],
      documentIsLoading,
      documentIsFetching,
      selectedDocumentId,
    );

    return data;
  }, [
    beneficiary,
    documentsCategories,
    documentIsLoading,
    documentIsFetching,
    selectedDocumentId,
  ]);

  const contractsFormattedData = useMemo(() => {
    const nextBeneficiaryContracts = beneficiaryContracts?.map((item) => ({
      ...item,
      url: generatePath(
        type === OrganizationType.INDIVIDUAL
          ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY_CONTRACT
          : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY_CONTRACT,
        {
          customerId: customerId as string,
          beneficiaryId: item.beneficiaryId,
          contractId: item.id,
        },
      ),
    }));

    const data = formatBeneficiaryContractsList(
      nextBeneficiaryContracts as BeneficiaryContract[],
      baseCurrency,
      currenciesList,
    );

    return data;
  }, [beneficiaryContracts, currenciesList, baseCurrency]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedDocumentId('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (beneficiary) {
      setBeneficiaryStatus(beneficiary.status);

      if (beneficiary?.paymentType === 'CASH_OVER_COUNTER') {
        getBeneficiaryDocumentsCategories();
      }
    }
  }, [beneficiary]);

  useEffect(() => {
    if (beneficiaryId) {
      getBeneficiary(beneficiaryId);
      getBeneficiaryContracts(beneficiaryId);
    }
  }, [beneficiaryId]);

  useEffect(() => {
    if (beneficiary) {
      onValidateCustomerBeneficiary(beneficiary.id);
    }
  }, [beneficiary]);

  return {
    isLoading,
    isFetching,
    errors,
    statusInProgress,
    beneficiaryStatus,
    onSaveStatus,
    updateStatusError,
    setBeneficiaryStatus,
    setRejectPopupVisible,
    globalValidateError,
    contractsFormattedData,
    filters,
    contractsIsLoading,
    contractsIsFetching,
    formattedDocumentsData,
    documentsCategoriesIsLoading,
    documentsCategoriesIsFetching,
    onRowClick,
    rejectPopupVisible,
    setRejectComment,
    rejectComment,
    setRejectCommentError,
    rejectCommentError,
  };
};

export default useBeneficiary;
